import { ArrowUpRight } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";
import type { CommunityNote } from "../../../api/community";
import { usePinnedTrack } from "../../../hooks/usePinnedTrack";
import { useScrollEnter } from "../../../hooks/useScrollEnter";
import { matchCharacterNotes, materialCharacters } from "../home-characters-data";
import { HomeCharacterScene } from "./HomeCharacterScene";
import { HomeSkip } from "./HomeSkip";

type HomeMaterialCharactersProps = {
  notes: CommunityNote[];
  loading: boolean;
};

export function HomeMaterialCharacters({ notes, loading }: HomeMaterialCharactersProps) {
  const scenes = useMemo(() => matchCharacterNotes(materialCharacters, notes), [notes]);
  const track = usePinnedTrack<HTMLElement, HTMLDivElement>(materialCharacters.length, 1.6);
  // 卡片盖上来的过程中（舞台顶边从视口底部升到顶部）逐行露出标题。
  const stageRef = useScrollEnter<HTMLDivElement>(0);

  return (
    <section
      ref={track.sectionRef}
      id="home-characters"
      className={`home-characters${track.pinned ? " is-pinned" : ""}`}
      aria-labelledby="home-characters-title"
    >
      <div ref={stageRef} className="home-characters-stage">
        <div ref={track.trackRef} className="home-characters-track">
          <header className="home-characters-intro">
            <h2 id="home-characters-title"><span>六种材质，</span><span>六种性格</span></h2>
            <p>继续向下滚动，材质会横向驶过。每块样本都标注了光泽与类别，在情绪板里可以直接赋给模型。</p>
          </header>
          {materialCharacters.map((item) => (
            <article className="home-character" key={item.id} aria-labelledby={`home-character-${item.id}`}>
              <div className="home-character-visual">
                <div className={`home-character-slab is-${item.shape} m-${item.id}`} role="img" aria-label={`${item.name}材质样本`} />
                <HomeCharacterScene materialName={item.name} note={scenes.get(item.id)} loading={loading} />
              </div>
              <div className="home-character-copy">
                <span className="home-character-en" lang="en">{item.english}</span>
                <h3 id={`home-character-${item.id}`}>{item.name}</h3>
                <p>{item.description}</p>
                <ul aria-label="光泽与类别">
                  {item.tags.map((tag) => <li key={tag}>{tag}</li>)}
                </ul>
              </div>
            </article>
          ))}
          <Link className="home-characters-more" to="/library?kind=material">
            <span>在材质库里<br />看全部样本</span>
            <ArrowUpRight size={34} strokeWidth={1.4} />
          </Link>
        </div>
        <HomeSkip targetId="featured" label="跳过，看社区精选" className="is-characters" visible={track.pinned} />
      </div>
    </section>
  );
}
